import React, { useEffect, useMemo, useState } from "react";
import { useStore } from "react-redux";

import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import DefaultOrg from "../assets/world-health.png";

import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";

import { resolveName } from "../utils/messageUtils";
import { calculateAge } from "../utils/dateUtils";
import styles from "./CardHeader.module.css";
import { Field } from "../utils/renderUtils";

/**
 * CardHeader
 * ----------
 * Infers subject / author / organization directly from a FHIR resource
 */
export default function CardHeader({ resource, handleShowNested, fhirClient }) {
    const store = useStore();

    const [patient, setPatient] = useState(null);
    const [author, setAuthor] = useState(null);
    const [organization, setOrganization] = useState(null);

    /* ===================== REFERENCE DISCOVERY ===================== */

    const references = useMemo(() => {
        if (!resource) return {};

        const resourceType = resource.resourceType;

        const isDocumentReference = resourceType === "DocumentReference";
        const isDiagnosticReport = resourceType === "DiagnosticReport";
        const isSpecimen = resourceType === "Specimen";

        const performerPractitioner =
            resource.performer?.find(p =>
                p.reference?.startsWith("Practitioner/") ||
                p.reference?.startsWith("PractitionerRole/")
            );

        const performerOrganization =
            resource.performer?.find(p =>
                p.reference?.startsWith("Organization/")
            );

        return {
            /* -------------------- SUBJECT -------------------- */
            subject:
                resource.subject ||
                resource.patient,

            /* -------------------- AUTHOR / PERFORMER / COLLECTOR -------------------- */
            author:
                isDocumentReference
                    ? resource.author?.[0]

                : isDiagnosticReport
                    ? resource.resultsInterpreter?.[0]

                : isSpecimen
                    ? resource.collection?.collector

                : (
                    resource.author ||
                    performerPractitioner ||
                    resource.performer?.[0] ||
                    resource.recorder ||
                    resource.requester
                ),

            /* -------------------- ORGANIZATION -------------------- */
            organization:
                resource.organization ||
                performerOrganization ||
                resource.performer?.[0]?.organization ||
                resource.author?.organization
        };
    }, [resource]);

    /* ===================== FETCH ENTITIES ===================== */

    useEffect(() => {
        if (references.subject?.reference && fhirClient && store) {
            fetchPatientByReference(
                references.subject.reference,
                fhirClient,
                store
            ).then(setPatient);
        }
    }, [references.subject, fhirClient, store]);

    useEffect(() => {
        if (references.author?.reference && fhirClient && store) {
            fetchProviderByReference(
                references.author.reference,
                fhirClient,
                store
            ).then(setAuthor);
        }
    }, [references.author, fhirClient, store]);

    useEffect(() => {
        if (references.organization?.reference && fhirClient && store) {
            fetchOrganizationByReference(
                references.organization.reference,
                fhirClient,
                store
            ).then(setOrganization);
        }
    }, [references.organization, fhirClient, store]);

    /* ===================== DISPLAY HELPERS ===================== */

    const patientName = resolveName(patient) || "Unknown Patient";
    const providerName = resolveName(author) || "Unknown Provider";
    const orgDisplayName = resolveName(organization) || "Unknown Organization";

    const patientPhotoUrl =
        patient?.photo?.[0]?.url ||
        (patient?.gender === "female" ? FemalePatient : MalePatient);

    const providerPhotoUrl =
        author?.photo?.[0]?.url ||
        (author?.gender === "female" ? FemaleProvider : MaleProvider);

    const practitionerLabel = (() => {
        switch (resource?.resourceType) {
            case "Specimen":
                return "Collector";
            case "Observation":
                return "Performer";
            default:
                return "Author / Performer";
        }
    })();

    const orgIcon = DefaultOrg;

    /* ===================== RENDER ===================== */

    return (
        <div className={styles.cardHeaderContainer}>
            <div className={styles.headerRow}>

                {/* ===================== PATIENT ===================== */}
                <div className={styles.entityColumn}>
                    <div className={styles.entityBlock}>
                        <button
                            className={styles.entityImageButton}
                            onClick={() =>
                                handleShowNested?.(patient, patientName)
                            }
                        >
                            <img
                                src={patientPhotoUrl}
                                alt="Patient"
                                className={styles.avatar}
                            />
                        </button>

                        <div>
                            <Field label="Patient">{patientName}</Field>
                            <Field label="DOB">{patient?.birthDate || "—"}</Field>
                            <Field label="Age">
                                {calculateAge(patient?.birthDate) || "Unknown"}
                            </Field>
                        </div>
                    </div>
                </div>

                {/* ===================== PROVIDER ===================== */}
                <div className={styles.entityColumn}>
                    <div className={styles.entityBlock}>
                        <button
                            className={styles.entityImageButton}
                            onClick={() =>
                                handleShowNested?.(author, providerName)
                            }
                        >
                            <img
                                src={providerPhotoUrl}
                                alt="Provider"
                                className={styles.avatar}
                            />
                        </button>

                        <div className={styles.orgText}>
                            <Field label={practitionerLabel}>
                                {providerName}
                            </Field>
                            <Field label="Active">
                                {author
                                    ? author.active
                                        ? "Yes"
                                        : "No"
                                    : "N/A"}
                            </Field>
                        </div>
                    </div>
                </div>

                {/* ===================== ORGANIZATION ===================== */}
                <div className={styles.entityColumn}>
                    <div className={styles.entityBlock}>
                        <button
                            className={styles.entityImageButton}
                            onClick={() =>
                                handleShowNested?.(
                                    organization,
                                    orgDisplayName
                                )
                            }
                        >
                            <img
                                src={orgIcon}
                                alt="Organization"
                                className={styles.avatar}
                            />
                        </button>

                        <div className={styles.orgText}>
                            <div className={styles.orgName}>
                                {orgDisplayName}
                            </div>
                            <div className={styles.orgType}>
                                {organization?.type?.text ||
                                    organization?.type?.coding?.[0]?.display ||
                                    ""}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <br />
        </div>
    );
}
